import { apiFetch as fetch } from "../lib/security";
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from "react";

const storageKey = "depositSeedHouse.sessionToken";

export type GuestAccount = {
  id: string;
  name: string;
  email: string;
  phone: string;
  marketingOptIn: boolean;
  createdAt?: string;
};

export type AccountBooking = {
  id: string;
  reference?: string;
  accountId?: string | null;
  apartmentSlug: string;
  apartmentTitle: string;
  start: string;
  end: string;
  adults: number;
  children: number;
  guests: number;
  guestName: string;
  guestEmail: string;
  guestPhone: string;
  note: string;
  status: "hold" | "confirmed" | "cancelled";
  holdExpiresAt?: string;
  nightlySubtotal: number | null;
  cleaningFee: number | null;
  total: number | null;
  marketingOptIn: boolean;
  createdAt?: string;
};

type SessionResponse = {
  account: GuestAccount;
  bookings?: AccountBooking[];
  sessionToken?: string;
  sessionExpiresAt?: string;
};

type BookingActionResponse = {
  booking: AccountBooking;
  bookings?: AccountBooking[];
};

type RegisterInput = {
  name: string;
  email: string;
  phone: string;
  password: string;
  marketingOptIn: boolean;
};

type LoginInput = {
  email: string;
  password: string;
};

type ProfileInput = {
  name: string;
  phone: string;
  marketingOptIn: boolean;
};

type PasswordResetConfirmInput = {
  token: string;
  password: string;
};

type ApiError = Error & {
  status?: number;
};

type AuthContextValue = {
  account: GuestAccount | null;
  bookings: AccountBooking[];
  sessionToken: string;
  loading: boolean;
  register: (input: RegisterInput) => Promise<void>;
  login: (input: LoginInput) => Promise<void>;
  logout: () => Promise<void>;
  updateProfile: (input: ProfileInput) => Promise<void>;
  cancelBooking: (bookingId: string) => Promise<AccountBooking>;
  requestPasswordReset: (email: string) => Promise<void>;
  confirmPasswordReset: (input: PasswordResetConfirmInput) => Promise<void>;
  refreshAccount: () => Promise<void>;
  applySession: (response: SessionResponse) => void;
};

const AuthContext = createContext<AuthContextValue | null>(null);

function storedToken() {
  if (typeof window === "undefined") {
    return "";
  }

  return window.localStorage.getItem(storageKey) ?? "";
}

function saveToken(token: string) {
  if (typeof window === "undefined") {
    return;
  }

  if (token) {
    window.localStorage.setItem(storageKey, token);
    return;
  }

  window.localStorage.removeItem(storageKey);
}

async function readJson(response: Response) {
  const data = await response.json().catch(() => null);

  if (!response.ok) {
    const error = new Error(data?.error?.message ?? "Request failed") as ApiError;
    error.status = response.status;
    throw error;
  }

  return data;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [sessionToken, setSessionToken] = useState(storedToken);
  const [account, setAccount] = useState<GuestAccount | null>(null);
  const [bookings, setBookings] = useState<AccountBooking[]>([]);
  const [loading, setLoading] = useState(Boolean(sessionToken));

  const applySession = useCallback((response: SessionResponse) => {
    if (response.sessionToken) {
      saveToken(response.sessionToken);
      setSessionToken(response.sessionToken);
    }

    setAccount(response.account);
    setBookings(response.bookings ?? []);
    setLoading(false);
  }, []);

  const clearSession = useCallback(() => {
    saveToken("");
    setSessionToken("");
    setAccount(null);
    setBookings([]);
    setLoading(false);
  }, []);

  const requestWithToken = useCallback(
    async (path: string, init: RequestInit = {}) => {
      const headers = new Headers(init.headers);

      if (sessionToken) {
        headers.set("Authorization", `Bearer ${sessionToken}`);
      }

      return fetch(path, { ...init, headers });
    },
    [sessionToken],
  );

  const readProtectedJson = useCallback(
    async (response: Response) => {
      try {
        return await readJson(response);
      } catch (error) {
        if ((error as ApiError).status === 401) {
          clearSession();
        }

        throw error;
      }
    },
    [clearSession],
  );

  const refreshAccount = useCallback(async () => {
    if (!sessionToken) {
      clearSession();
      return;
    }

    setLoading(true);

    try {
      const response = await requestWithToken("/api/accounts/me");
      const data = (await readJson(response)) as SessionResponse;

      setAccount(data.account);
      setBookings(data.bookings ?? []);
    } catch {
      clearSession();
    } finally {
      setLoading(false);
    }
  }, [clearSession, readProtectedJson, requestWithToken, sessionToken]);

  useEffect(() => {
    void refreshAccount();
  }, [refreshAccount]);

  const register = useCallback(
    async (input: RegisterInput) => {
      const response = await fetch("/api/accounts/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      const data = (await readJson(response)) as SessionResponse;

      applySession(data);
    },
    [applySession],
  );

  const login = useCallback(
    async (input: LoginInput) => {
      const response = await fetch("/api/accounts/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      const data = (await readJson(response)) as SessionResponse;

      applySession(data);
    },
    [applySession],
  );

  const logout = useCallback(async () => {
    const token = sessionToken;

    clearSession();

    if (!token) {
      return;
    }

    await fetch("/api/accounts/logout", {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
    }).catch(() => {});
  }, [clearSession, sessionToken]);

  const updateProfile = useCallback(
    async (input: ProfileInput) => {
      const response = await requestWithToken("/api/accounts/me", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      const data = (await readProtectedJson(response)) as SessionResponse;

      setAccount(data.account);
      setBookings(data.bookings ?? []);
    },
    [readProtectedJson, requestWithToken],
  );

  const cancelBooking = useCallback(
    async (bookingId: string) => {
      const response = await requestWithToken(`/api/accounts/bookings/${encodeURIComponent(bookingId)}/cancel`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      const data = (await readProtectedJson(response)) as BookingActionResponse;

      setBookings(data.bookings ?? bookings.map((booking) => (booking.id === data.booking.id ? data.booking : booking)));

      return data.booking;
    },
    [bookings, readProtectedJson, requestWithToken],
  );

  const requestPasswordReset = useCallback(async (email: string) => {
    const response = await fetch("/api/accounts/password-reset/request", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    });

    await readJson(response);
  }, []);

  const confirmPasswordReset = useCallback(
    async (input: PasswordResetConfirmInput) => {
      const response = await fetch("/api/accounts/password-reset/confirm", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      const data = (await readJson(response)) as SessionResponse;

      applySession(data);
    },
    [applySession],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      account,
      bookings,
      sessionToken,
      loading,
      register,
      login,
      logout,
      updateProfile,
      cancelBooking,
      requestPasswordReset,
      confirmPasswordReset,
      refreshAccount,
      applySession,
    }),
    [
      account,
      applySession,
      bookings,
      cancelBooking,
      confirmPasswordReset,
      loading,
      login,
      logout,
      refreshAccount,
      register,
      requestPasswordReset,
      sessionToken,
      updateProfile,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }

  return context;
}
